import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import api from '../../api';

// ==================== Initial State ====================
const initialState = {
  doctorOPDAssessments: [],
  currentDoctorOPDAssessment: null,
  isLoading: false,
  error: null,
};

// ==================== Async Thunks ====================

// Fetch all doctor OPD assessments
export const fetchDoctorOPDAssessments = createAsyncThunk(
  'doctorOPDAssessments/fetchDoctorOPDAssessments',
  async (patientId = null, { rejectWithValue }) => {
    try {
      const url = patientId
        ? `/doctor-opd-assessments?patient_id=${patientId}`
        : '/doctor-opd-assessments';
      const response = await api.get(url);
      return response.data.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.error || 'Failed to fetch Doctor OPD assessments'
      );
    }
  }
);

// Fetch single doctor OPD assessment
export const fetchDoctorOPDAssessment = createAsyncThunk(
  'doctorOPDAssessments/fetchDoctorOPDAssessment',
  async (id, { rejectWithValue }) => {
    try {
      const response = await api.get(`/doctor-opd-assessments/${id}`);
      return response.data.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.error || 'Failed to fetch Doctor OPD assessment'
      );
    }
  }
);

// Create new doctor OPD assessment
export const createDoctorOPDAssessment = createAsyncThunk(
  'doctorOPDAssessments/createDoctorOPDAssessment',
  async (assessmentData, { rejectWithValue }) => {
    try {
      const response = await api.post(`/doctor-opd-assessments`, assessmentData);
      return response.data.data;
    } catch (error) {
      if (error.response?.status === 422) return rejectWithValue(error.response.data.errors);
      return rejectWithValue(
        error.response?.data?.error || 'Failed to create Doctor OPD assessment'
      );
    }
  }
);

// Update doctor OPD assessment
export const updateDoctorOPDAssessment = createAsyncThunk(
  'doctorOPDAssessments/updateDoctorOPDAssessment',
  async ({ id, assessmentData }, { rejectWithValue }) => {
    try {
      const response = await api.put(`/doctor-opd-assessments/${id}`, assessmentData);
      return response.data.data ?? response.data;
    } catch (error) {
      if (error.response?.status === 422) return rejectWithValue(error.response.data.errors);
      return rejectWithValue(
        error.response?.data?.error || 'Failed to update Doctor OPD assessment'
      );
    }
  }
);

// Delete doctor OPD assessment
export const deleteDoctorOPDAssessment = createAsyncThunk(
  'doctorOPDAssessments/deleteDoctorOPDAssessment',
  async (id, { rejectWithValue }) => {
    try {
      await api.delete(`/doctor-opd-assessments/${id}`);
      return id;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.error || 'Failed to delete Doctor OPD assessment'
      );
    }
  }
);

// ==================== Slice ====================
const doctorOPDAssessmentsSlice = createSlice({
  name: 'doctorOPDAssessments',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    setCurrentDoctorOPDAssessment: (state, action) => {
      state.currentDoctorOPDAssessment = action.payload;
    },
    clearCurrentDoctorOPDAssessment: (state) => {
      state.currentDoctorOPDAssessment = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // FETCH ALL
      .addCase(fetchDoctorOPDAssessments.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchDoctorOPDAssessments.fulfilled, (state, action) => {
        state.isLoading = false;
        state.doctorOPDAssessments = action.payload;
      })
      .addCase(fetchDoctorOPDAssessments.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })

      // FETCH SINGLE
      .addCase(fetchDoctorOPDAssessment.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchDoctorOPDAssessment.fulfilled, (state, action) => {
        state.isLoading = false;
        state.currentDoctorOPDAssessment = action.payload;
      })
      .addCase(fetchDoctorOPDAssessment.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })

      // CREATE
      .addCase(createDoctorOPDAssessment.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(createDoctorOPDAssessment.fulfilled, (state, action) => {
        state.isLoading = false;
        state.doctorOPDAssessments.unshift(action.payload);
      })
      .addCase(createDoctorOPDAssessment.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })

      // UPDATE
      .addCase(updateDoctorOPDAssessment.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(updateDoctorOPDAssessment.fulfilled, (state, action) => {
        state.isLoading = false;
        const updated = action.payload.data ?? action.payload;

        const index = state.doctorOPDAssessments.findIndex((d) => d.id === updated.id);
        if (index !== -1) state.doctorOPDAssessments[index] = updated;

        if (state.currentDoctorOPDAssessment?.id === updated.id) {
          state.currentDoctorOPDAssessment = updated;
        }
      })
      .addCase(updateDoctorOPDAssessment.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })

      // DELETE
      .addCase(deleteDoctorOPDAssessment.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(deleteDoctorOPDAssessment.fulfilled, (state, action) => {
        state.isLoading = false;
        state.doctorOPDAssessments = state.doctorOPDAssessments.filter(
          (d) => d.id !== action.payload
        );

        if (state.currentDoctorOPDAssessment?.id === action.payload) {
          state.currentDoctorOPDAssessment = null;
        }
      })
      .addCase(deleteDoctorOPDAssessment.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      });
  },
});

export const { clearError, setCurrentDoctorOPDAssessment, clearCurrentDoctorOPDAssessment } =
  doctorOPDAssessmentsSlice.actions;
export default doctorOPDAssessmentsSlice.reducer;
